import { useFrame } from '@react-three/fiber';
import { type RefObject, useCallback, useMemo, useRef } from 'react';
import { useShallow } from 'zustand/shallow';

import {
    isTopBottomCameraView,
    raycastPlane
} from 'app/modules/wallforming/components/scenes/objects/articles/utils/dragUtils';
import { PoiFaces } from 'app/modules/wallforming/components/scenes/objects/pois/PoiFaces';
import { PoiPoints } from 'app/modules/wallforming/components/scenes/objects/pois/PoiPoints';
import { PoiPointSelection } from 'app/modules/wallforming/components/scenes/objects/pois/PoiPointSelection';
import { PoiBatchedEntry } from 'app/modules/wallforming/components/scenes/objects/pois/types/poiBatchedEntry';
import { PoiPointEntry } from 'app/modules/wallforming/components/scenes/objects/pois/types/poiPointEntry';
import {
    isCornerSingleSidePoi,
    isPlanarFacePoi,
    createWallFaceMatrix,
    createCornerMatrix,
    EDGE_COLOR,
    SELECTION_COLOR,
    FACE_COLOR
} from 'app/modules/wallforming/components/scenes/objects/pois/utils/poiUtils';
import { InteractionMode } from 'app/modules/wallforming/components/scenes/types/interactionMode';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

import type { Line } from 'api/models';
import type { Group } from 'three';

export const PoiNavigator = () => {
    const { pois, interactionMode } = usePlacementStore(
        useShallow((state) => ({
            pois: state.pois,
            interactionMode: state.interactionMode
        }))
    );

    const isTopBottomViewRef = useRef<boolean>(isTopBottomCameraView(raycastPlane.normal));
    const pointsGroupRef = useRef<Group | null>(null);
    const facesGroupRef = useRef<Group | null>(null);

    // Create data for the POI faces and points based on the current POIs
    const { faceEntries, pointEntries } = useMemo(() => {
        const entries: PoiBatchedEntry[] = [];
        const pointEntries: PoiPointEntry[] = [];

        pois.forEach((poi, poiIndex) => {
            if (!poi?.poi) {
                return;
            }

            if (isPlanarFacePoi(poi.poi) && poi.poi.formFace?.outerWire?.edges?.length) {
                entries.push({
                    poiIndex,
                    matrix: createWallFaceMatrix(poi.poi.formFace.outerWire.edges as Line[], poi.poi.formFace.normal),
                    defaultColor: FACE_COLOR.clone(),
                    selectedColor: SELECTION_COLOR.clone(),
                    filterType: poi.filterType
                });
                if (poi.poi.navigationPoint) {
                    pointEntries.push({
                        poiIndex,
                        filterType: poi.filterType,
                        x: poi.poi.navigationPoint.x,
                        y: poi.poi.navigationPoint.y,
                        z: poi.poi.navigationPoint.z,
                        scale: 1,
                        hoverColor: poi.hoverColor,
                        defaultColor: poi.defaultColor
                    });
                }
                return;
            }

            if (isCornerSingleSidePoi(poi.poi)) {
                entries.push({
                    poiIndex,
                    matrix: createCornerMatrix(poi.poi.topPoint, poi.poi.bottomPoint),
                    defaultColor: EDGE_COLOR.clone(),
                    selectedColor: SELECTION_COLOR.clone(),
                    filterType: poi.filterType
                });
                if (poi.poi.navigationPoint) {
                    pointEntries.push({
                        poiIndex,
                        filterType: poi.filterType,
                        x: poi.poi.navigationPoint.x,
                        y: poi.poi.navigationPoint.y,
                        z: poi.poi.navigationPoint.z,
                        scale: 1,
                        hoverColor: poi.hoverColor,
                        defaultColor: poi.defaultColor
                    });
                }
            }
        });

        return { faceEntries: entries, pointEntries };
    }, [pois]);

    const handleGroupVisibility = useCallback((groupRef: RefObject<Group | null>, visible: boolean) => {
        if (groupRef.current) {
            groupRef.current.visible = visible;
        }
    }, []);

    // Handle refs and visibility of the POI points and faces groups on mount
    const handlePointsGroupRef = useCallback(
        (group: Group | null) => {
            pointsGroupRef.current = group;
            handleGroupVisibility(pointsGroupRef, isTopBottomViewRef.current);
        },
        [handleGroupVisibility]
    );

    const handleFacesGroupRef = useCallback(
        (group: Group | null) => {
            facesGroupRef.current = group;
            handleGroupVisibility(facesGroupRef, !isTopBottomViewRef.current);
        },
        [handleGroupVisibility]
    );

    // Handle visibility of the POI points and faces groups on camera view change
    useFrame(() => {
        const currentView = isTopBottomCameraView(raycastPlane.normal);
        if (isTopBottomViewRef.current !== currentView) {
            isTopBottomViewRef.current = currentView;
            handleGroupVisibility(pointsGroupRef, currentView);
            handleGroupVisibility(facesGroupRef, !currentView);
        }
    });

    if (faceEntries.length === 0 || interactionMode !== InteractionMode.POI_NAVIGATION) {
        return null;
    }

    return (
        <>
            {/* POI points at nearly top/bottom view */}
            <group ref={handlePointsGroupRef}>
                <PoiPoints entries={pointEntries} />
                <PoiPointSelection />
            </group>

            {/* POI faces/edges */}
            <group ref={handleFacesGroupRef}>
                <PoiFaces entries={faceEntries} />
            </group>
        </>
    );
};
